import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft, CheckCircle2, Clock, CalendarDays, MapPin,
  Ticket, Users, RefreshCw, QrCode, XCircle, RotateCcw,
  AlertTriangle, Trash2, UserPlus, Pencil, Undo2,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminEventGuests() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState('live'); // 'live' | 'cancelled'

  const [cancelDialog, setCancelDialog] = useState(null);
  const [refundDialog, setRefundDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [actioning, setActioning] = useState(false);

  const EMPTY_GUEST = { customer_name: '', customer_email: '', customer_phone: '', ticket_count: '1', payment_status: 'manual', notes: '' };
  const [addGuestOpen, setAddGuestOpen] = useState(false);
  const [guestForm, setGuestForm] = useState(EMPTY_GUEST);
  const [addingSaving, setAddingSaving] = useState(false);

  const [editDialog, setEditDialog] = useState(null); // booking object
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [undoingCheckIn, setUndoingCheckIn] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await axios.get(`${API}/admin/events/${eventId}/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      toast.error('Failed to load guest list');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [eventId, token]);

  useEffect(() => { load(); }, [load]);

  const handleCheckIn = async (ticketId) => {
    if (!data?.event) return;
    setCheckingIn(ticketId);
    try {
      const res = await axios.post(
        `${API}/admin/events/${data.event.id}/tickets/${ticketId}/check-in`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast[res.data.already_scanned ? 'warning' : 'success'](
        res.data.already_scanned ? 'Already scanned' : 'Ticket checked in ✓'
      );
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to check in');
    } finally {
      setCheckingIn(null);
    }
  };

  const handleCancel = async () => {
    if (!cancelDialog) return;
    setActioning(true);
    try {
      await axios.delete(
        `${API}/admin/events/${cancelDialog.event_id}/bookings/${cancelDialog.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Booking cancelled');
      setCancelDialog(null);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setActioning(false);
    }
  };

  const handleRefund = async () => {
    if (!refundDialog) return;
    setActioning(true);
    try {
      const res = await axios.post(
        `${API}/admin/events/${refundDialog.event_id}/bookings/${refundDialog.id}/refund`,
        { reason: 'requested_by_customer' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Refund of £${res.data.amount_refunded?.toFixed(2)} issued`);
      setRefundDialog(null);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refund failed');
    } finally {
      setActioning(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    setActioning(true);
    try {
      await axios.delete(
        `${API}/admin/events/${deleteDialog.event_id}/bookings/${deleteDialog.id}/permanent`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Booking permanently deleted');
      setDeleteDialog(null);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setActioning(false);
    }
  };

  const handleAddGuest = async () => {
    if (!guestForm.customer_name.trim()) { toast.error('Name is required'); return; }
    const count = parseInt(guestForm.ticket_count);
    if (!count || count < 1) { toast.error('Ticket count must be at least 1'); return; }
    setAddingSaving(true);
    try {
      await axios.post(
        `${API}/admin/events/${eventId}/bookings/manual`,
        {
          customer_name: guestForm.customer_name.trim(),
          customer_email: guestForm.customer_email.trim() || null,
          customer_phone: guestForm.customer_phone.trim() || null,
          ticket_count: count,
          payment_status: guestForm.payment_status,
          notes: guestForm.notes.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${guestForm.customer_name} added to guest list`);
      setAddGuestOpen(false);
      setGuestForm(EMPTY_GUEST);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add guest');
    } finally {
      setAddingSaving(false);
    }
  };

  const openEditDialog = (booking) => {
    setEditForm({
      customer_name: booking.customer_name || '',
      customer_email: booking.customer_email || '',
      customer_phone: booking.customer_phone || '',
      payment_status: booking.payment_status || 'manual',
      notes: booking.notes || '',
    });
    setEditDialog(booking);
  };

  const handleEditSave = async () => {
    if (!editDialog) return;
    setEditSaving(true);
    try {
      await axios.patch(
        `${API}/admin/events/${editDialog.event_id}/bookings/${editDialog.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Booking updated');
      setEditDialog(null);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update booking');
    } finally {
      setEditSaving(false);
    }
  };

  const handleUndoCheckIn = async (ticket, eventId) => {
    setUndoingCheckIn(ticket.id);
    try {
      await axios.delete(
        `${API}/admin/events/${eventId}/tickets/${ticket.id}/check-in`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Check-in reversed');
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to undo check-in');
    } finally {
      setUndoingCheckIn(null);
    }
  };

  const event = data?.event;
  const allBookings = data?.bookings || [];
  const liveBookings = allBookings.filter(b => !b.cancelled && !b.refunded);
  const cancelledBookings = allBookings.filter(b => b.cancelled || b.refunded);
  const visibleBookings = tab === 'live' ? liveBookings : cancelledBookings;

  const allTickets = allBookings.flatMap(b => b.tickets || []);
  const totalSold = liveBookings.reduce((s, b) => s + (b.ticket_count || 0), 0);
  const totalCheckedIn = allTickets.filter(t => t.checked_in && !t.cancelled).length;
  const totalRemaining = Math.max(0, (event?.capacity || 0) - (event?.tickets_sold || 0));

  const paymentLabel = (status) => {
    if (status === 'free')          return { text: 'Free',          cls: 'bg-slate-100 text-slate-600' };
    if (status === 'paid')          return { text: 'Paid',          cls: 'bg-emerald-100 text-emerald-700' };
    if (status === 'refunded')      return { text: 'Refunded',      cls: 'bg-purple-100 text-purple-700' };
    if (status === 'manual')        return { text: 'Phone / Cash',  cls: 'bg-orange-100 text-orange-700' };
    if (status === 'complimentary') return { text: 'Complimentary', cls: 'bg-sky-100 text-sky-700' };
    return { text: 'Pending', cls: 'bg-amber-100 text-amber-700' };
  };

  const BookingCard = ({ booking }) => {
    const isCancelled = booking.cancelled;
    const isRefunded  = booking.refunded;
    const isPaid      = booking.payment_status === 'paid';
    const pl          = paymentLabel(booking.payment_status);
    const allIn       = (booking.tickets || []).filter(t => !t.cancelled).every(t => t.checked_in);
    const bookedAt    = booking.created_at ? format(new Date(booking.created_at), 'dd MMM yyyy, HH:mm') : '';
    const canCancel   = !isCancelled && !isRefunded;
    const canRefund   = isPaid && !isRefunded;
    const canDelete   = isCancelled || isRefunded;

    return (
      <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${
        isCancelled ? 'border-red-200' :
        isRefunded  ? 'border-purple-200' :
        allIn       ? 'border-emerald-200' : 'border-slate-200'
      }`}>
        {/* Header */}
        <div className="flex items-start gap-3 p-4 pb-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm ${
            isCancelled ? 'bg-red-100 text-red-600' :
            isRefunded  ? 'bg-purple-100 text-purple-600' :
            allIn       ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
          }`}>
            {booking.ticket_count}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-bold text-slate-900">{booking.customer_name}</p>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${pl.cls}`}>{pl.text}</span>
              {booking.source === 'manual' && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Phone / Manual</span>
              )}
              {isCancelled && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-600">Cancelled</span>}
              {isRefunded  && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Refunded</span>}
              {!isCancelled && !isRefunded && allIn && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />All in
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 truncate">{booking.customer_email || booking.customer_phone || '—'}</p>
            {booking.notes && <p className="text-xs text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 mt-1 inline-block">{booking.notes}</p>}
            {bookedAt && <p className="text-xs text-slate-400 mt-0.5">Added {bookedAt}{booking.added_by ? ` by ${booking.added_by}` : ''}</p>}
            {isRefunded && booking.refunded_at && (
              <p className="text-xs text-purple-500 mt-0.5">
                Refunded {format(new Date(booking.refunded_at), 'dd MMM, HH:mm')}
                {booking.refunded_by ? ` by ${booking.refunded_by}` : ''}
              </p>
            )}
            {isCancelled && booking.cancelled_at && (
              <p className="text-xs text-red-400 mt-0.5">
                Cancelled {format(new Date(booking.cancelled_at), 'dd MMM, HH:mm')}
                {booking.cancelled_by ? ` by ${booking.cancelled_by}` : ''}
              </p>
            )}
          </div>
        </div>

        {/* Per-ticket rows */}
        <div className="border-t border-slate-100">
          {(booking.tickets || []).map((ticket) => (
            <div
              key={ticket.id}
              className={`flex items-center justify-between px-4 py-3 ${
                ticket.cancelled    ? 'bg-red-50/40' :
                ticket.checked_in   ? 'bg-emerald-50/50' : 'bg-white'
              } border-b border-slate-100 last:border-b-0`}
            >
              <div className="flex items-center gap-3">
                <Ticket className={`h-4 w-4 flex-shrink-0 ${
                  ticket.cancelled  ? 'text-red-300' :
                  ticket.checked_in ? 'text-emerald-500' : 'text-slate-300'
                }`} />
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    Ticket {ticket.ticket_number} of {booking.ticket_count}
                    {ticket.cancelled && <span className="text-red-400 ml-1 text-xs">(cancelled)</span>}
                  </p>
                  {ticket.checked_in && ticket.checked_in_at && (
                    <p className="text-xs text-emerald-600">
                      Checked in {format(new Date(ticket.checked_in_at), 'HH:mm')}
                      {ticket.checked_in_by ? ` · ${ticket.checked_in_by}` : ''}
                    </p>
                  )}
                </div>
              </div>
              {!ticket.cancelled && (ticket.checked_in ? (
                <button
                  onClick={() => handleUndoCheckIn(ticket, booking.event_id)}
                  disabled={undoingCheckIn === ticket.id}
                  className="flex items-center gap-1 text-xs text-emerald-600 hover:text-red-500 transition-colors group"
                  title="Undo check-in"
                >
                  <CheckCircle2 className="h-5 w-5 group-hover:hidden" />
                  <Undo2 className="h-5 w-5 hidden group-hover:block" />
                  <span className="hidden group-hover:inline text-red-500">Undo</span>
                </button>
              ) : (
                <Button
                  size="sm" variant="outline"
                  disabled={checkingIn === ticket.id || isCancelled || isRefunded}
                  onClick={() => handleCheckIn(ticket.id)}
                  className="text-xs h-8"
                >
                  {checkingIn === ticket.id ? 'Checking in…' : 'Check in'}
                </Button>
              ))}
            </div>
          ))}
        </div>

        {/* Action bar */}
        <div className="flex flex-wrap gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50/60">
          {!isCancelled && !isRefunded && (
            <Button size="sm" variant="outline"
              className="gap-1.5 text-slate-600 border-slate-200 hover:bg-slate-100 text-xs"
              onClick={() => openEditDialog(booking)}
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Button>
          )}
          {canRefund && (
            <Button size="sm" variant="outline"
              className="gap-1.5 text-purple-600 border-purple-200 hover:bg-purple-50 text-xs"
              onClick={() => setRefundDialog(booking)}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Refund £{booking.total_amount?.toFixed(2)}
            </Button>
          )}
          {canCancel && (
            <Button size="sm" variant="outline"
              className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50 text-xs"
              onClick={() => setCancelDialog(booking)}
            >
              <XCircle className="h-3.5 w-3.5" />
              Cancel booking
            </Button>
          )}
          {canDelete && (
            <Button size="sm" variant="outline"
              className="gap-1.5 text-slate-500 border-slate-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200 text-xs ml-auto"
              onClick={() => setDeleteDialog(booking)}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete permanently
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/admin/events')} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
            <ArrowLeft className="h-5 w-5 text-slate-600" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Guest List</p>
            <h1 className="text-base font-bold text-slate-900 truncate">{event?.name || 'Loading…'}</h1>
          </div>
          <Button variant="ghost" size="sm" onClick={() => load(true)} disabled={refreshing} className="gap-1.5 text-slate-500">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {event && (
            <>
              <Button
                size="sm" variant="outline"
                className="gap-1.5 text-slate-700"
                onClick={() => { setGuestForm(EMPTY_GUEST); setAddGuestOpen(true); }}
              >
                <UserPlus className="h-4 w-4" />
                Add guest
              </Button>
              <a href={`/admin/events/${eventId}/check-in`} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="gap-1.5">
                  <QrCode className="h-4 w-4" />
                  Door scanner
                </Button>
              </a>
            </>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {loading && <div className="text-center text-slate-400 py-20">Loading…</div>}

        {!loading && data && (
          <>
            {/* Event summary */}
            {event && (
              <div className="bg-slate-900 text-white rounded-2xl p-5 mb-6">
                <h2 className="text-xl font-bold mb-3">{event.name}</h2>
                <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-slate-300">
                  <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-slate-500" />{event.date}</span>
                  <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-slate-500" />{event.time}</span>
                  {event.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-500" />{event.location}</span>}
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Ticket className="h-4 w-4 text-slate-400" />
                  <p className="text-3xl font-black text-slate-900">{totalSold}</p>
                </div>
                <p className="text-xs text-slate-500 font-medium">Active tickets</p>
              </div>
              <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-4 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  <p className="text-3xl font-black text-emerald-700">{totalCheckedIn}</p>
                </div>
                <p className="text-xs text-emerald-600 font-medium">Checked in</p>
              </div>
              <div className="bg-blue-50 rounded-2xl border border-blue-200 p-4 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Users className="h-4 w-4 text-blue-400" />
                  <p className="text-3xl font-black text-blue-700">{totalRemaining}</p>
                </div>
                <p className="text-xs text-blue-600 font-medium">Remaining</p>
              </div>
            </div>

            {/* Progress bar */}
            {totalSold > 0 && (
              <div className="mb-6">
                <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                  <span>{totalCheckedIn} checked in</span>
                  <span>{totalSold} active</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                    style={{ width: `${totalSold > 0 ? Math.round((totalCheckedIn / totalSold) * 100) : 0}%` }}
                  />
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl mb-5">
              <button
                onClick={() => setTab('live')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-semibold transition-all ${
                  tab === 'live' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Live bookings
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                  tab === 'live' ? 'bg-slate-900 text-white' : 'bg-slate-300 text-slate-600'
                }`}>
                  {liveBookings.length}
                </span>
              </button>
              <button
                onClick={() => setTab('cancelled')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-semibold transition-all ${
                  tab === 'cancelled' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Cancelled / Refunded
                {cancelledBookings.length > 0 && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                    tab === 'cancelled' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-600'
                  }`}>
                    {cancelledBookings.length}
                  </span>
                )}
              </button>
            </div>

            {/* Booking list */}
            {visibleBookings.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl">
                <Ticket className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">
                  {tab === 'live' ? 'No active bookings' : 'No cancelled bookings'}
                </p>
                {tab === 'live' && <p className="text-sm text-slate-400 mt-1">Share the event link to start taking RSVPs</p>}
              </div>
            ) : (
              <div className="space-y-3">
                {visibleBookings.map(b => <BookingCard key={b.id} booking={b} />)}
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit booking dialog */}
      <Dialog open={!!editDialog} onOpenChange={(v) => { if (!v) setEditDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-slate-600" />
              Edit booking
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Name</Label>
              <Input
                value={editForm.customer_name || ''}
                onChange={e => setEditForm(f => ({ ...f, customer_name: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Email</Label>
              <Input
                type="email"
                value={editForm.customer_email || ''}
                onChange={e => setEditForm(f => ({ ...f, customer_email: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Phone</Label>
              <Input
                type="tel"
                value={editForm.customer_phone || ''}
                onChange={e => setEditForm(f => ({ ...f, customer_phone: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Payment status</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={editForm.payment_status || 'manual'}
                onChange={e => setEditForm(f => ({ ...f, payment_status: e.target.value }))}
              >
                <option value="manual">Phone / Cash</option>
                <option value="paid">Paid</option>
                <option value="free">Free</option>
                <option value="complimentary">Complimentary</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input
                placeholder="e.g. VIP, accessibility needs…"
                value={editForm.notes || ''}
                onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditDialog(null)} disabled={editSaving}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={editSaving} className="gap-1.5">
              <Pencil className="h-4 w-4" />
              {editSaving ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={() => setCancelDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />Cancel booking
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-2">
            <p className="text-slate-700 text-sm">
              Cancel the booking for <strong>{cancelDialog?.customer_name}</strong>?
            </p>
            <p className="text-slate-500 text-sm">
              Invalidates all {cancelDialog?.ticket_count} ticket{cancelDialog?.ticket_count !== 1 ? 's' : ''}.
              {cancelDialog?.payment_status === 'paid' && ' Use Refund instead if you need to return money.'}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelDialog(null)} disabled={actioning}>Keep</Button>
            <Button variant="destructive" onClick={handleCancel} disabled={actioning} className="gap-1.5">
              <XCircle className="h-4 w-4" />
              {actioning ? 'Cancelling…' : 'Yes, cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund dialog */}
      <Dialog open={!!refundDialog} onOpenChange={() => setRefundDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <RotateCcw className="h-5 w-5" />Issue refund
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-2">
            <p className="text-slate-700 text-sm">
              Refund <strong>£{refundDialog?.total_amount?.toFixed(2)}</strong> to <strong>{refundDialog?.customer_name}</strong>?
            </p>
            <p className="text-slate-500 text-sm">
              Full refund via Stripe. All {refundDialog?.ticket_count} ticket{refundDialog?.ticket_count !== 1 ? 's' : ''} will be cancelled.
            </p>
            <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
              <p className="text-xs text-purple-700">Typically appears in 5–10 business days.</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRefundDialog(null)} disabled={actioning}>Cancel</Button>
            <Button onClick={handleRefund} disabled={actioning} className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white">
              <RotateCcw className="h-4 w-4" />
              {actioning ? 'Processing…' : `Refund £${refundDialog?.total_amount?.toFixed(2)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add guest dialog */}
      <Dialog open={addGuestOpen} onOpenChange={(v) => { if (!v) setAddGuestOpen(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-slate-600" />
              Add guest manually
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Name *</Label>
              <Input
                placeholder="Full name"
                value={guestForm.customer_name}
                onChange={e => setGuestForm(f => ({ ...f, customer_name: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Email <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Input
                type="email"
                placeholder="customer@example.com"
                value={guestForm.customer_email}
                onChange={e => setGuestForm(f => ({ ...f, customer_email: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Phone <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Input
                type="tel"
                placeholder="07700 000000"
                value={guestForm.customer_phone}
                onChange={e => setGuestForm(f => ({ ...f, customer_phone: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Tickets *</Label>
                <Input
                  type="number"
                  min="1"
                  value={guestForm.ticket_count}
                  onChange={e => setGuestForm(f => ({ ...f, ticket_count: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label>Payment</Label>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={guestForm.payment_status}
                  onChange={e => setGuestForm(f => ({ ...f, payment_status: e.target.value }))}
                >
                  <option value="manual">Phone / Cash</option>
                  <option value="paid">Paid</option>
                  <option value="complimentary">Complimentary</option>
                  <option value="free">Free</option>
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Notes <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Input
                placeholder="e.g. VIP, accessibility needs…"
                value={guestForm.notes}
                onChange={e => setGuestForm(f => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAddGuestOpen(false)} disabled={addingSaving}>Cancel</Button>
            <Button onClick={handleAddGuest} disabled={addingSaving} className="gap-1.5">
              <UserPlus className="h-4 w-4" />
              {addingSaving ? 'Adding…' : 'Add to guest list'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" />Delete permanently
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-2">
            <p className="text-slate-700 text-sm">
              Permanently delete the booking for <strong>{deleteDialog?.customer_name}</strong>?
            </p>
            <p className="text-slate-500 text-sm">
              This removes it from the guest list entirely. It cannot be undone.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialog(null)} disabled={actioning}>Keep</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={actioning} className="gap-1.5">
              <Trash2 className="h-4 w-4" />
              {actioning ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
